import { TFunction } from 'i18next';
import { FOLDER_TYPES, FoldersV2 } from 'lib/api/folders/getFoldersV2';
import {
  ALL_FOLDERS_VALUE,
  DEFAULT_FOLDER_WITH_USER_ID,
  getAllFolders,
} from 'lib/const/Folders';

export const LAST_SELECTED_FOLDER_LOCAL_STORAGE_KEY = 'lastSelectedFolder';

interface LastSelectedFolder {
  folderId: string | number;
  userId: string;
}

export const getLastSelectedFolderFromLocalStorage =
  (): LastSelectedFolder | null => {
    const item = localStorage.getItem(LAST_SELECTED_FOLDER_LOCAL_STORAGE_KEY);
    return item ? JSON.parse(item) : null;
  };

export const setLastSelectedFolderToLocalStorage = (
  value: LastSelectedFolder
): void => {
  localStorage.setItem(
    LAST_SELECTED_FOLDER_LOCAL_STORAGE_KEY,
    JSON.stringify(value)
  );
};

export const isCompatibleUser = (
  loggedUserId: number,
  localStorageUserId: number
) => loggedUserId === localStorageUserId;

export const getSelectedFolder = (
  queryParamsFolderId: string,
  folders: FoldersV2[],
  userId: string,
  t: TFunction
) => {
  const lastSelected = getLastSelectedFolderFromLocalStorage();

  const loggedUserId = Number(userId);
  const compatibleUser = Number(lastSelected?.userId) === loggedUserId;

  // Helper to get specific folder (All Folders or Primary)
  const getVirtualFolder = (id: string) => {
    if (id === ALL_FOLDERS_VALUE) {
      return {
        folderId: ALL_FOLDERS_VALUE,
        name: getAllFolders(t).label,
        isDeletable: false,
        isFolderShared: false,
        userId: loggedUserId,
        folderType: FOLDER_TYPES.PERSONAL,
        access: undefined,
      };
    }
    return DEFAULT_FOLDER_WITH_USER_ID(userId);
  };

  // Helper to resolve an ID to either a folder from folders or All Folders or Primary
  const resolveFolder = (id?: string | number) => {
    if (!id) return null;

    // Check for (_all or -2) folders first
    if (id === ALL_FOLDERS_VALUE || id === '-2') {
      return getVirtualFolder(id);
    }

    // Otherwise, find in the folders array
    return folders.find(f => f.folderId.toString() === id);
  };

  // Prioritize Query Params
  const folderFromQuery = resolveFolder(queryParamsFolderId);
  if (folderFromQuery && compatibleUser) return folderFromQuery;

  // Fallback to LocalStorage
  const folderFromStorage = resolveFolder(lastSelected?.folderId);
  if (folderFromStorage && compatibleUser) return folderFromStorage;

  // 3. Final Default
  return DEFAULT_FOLDER_WITH_USER_ID(userId);
};
